import React, { useEffect } from "react";
import { api } from "../lib/api";

/**
 * Loads site_settings.head_scripts from the backend and injects them once into <head>.
 * Also injects Google Analytics gtag if `google_analytics_id` is set.
 * NOTE: head_scripts is plain HTML configured by admins — it is intentionally trusted.
 */
export default function HeadScripts() {
  useEffect(() => {
    let mounted = true;
    const injected = [];
    api.get("/site_settings").then(({ data }) => {
      if (!mounted) return;
      // Google Analytics
      if (data.google_analytics_id) {
        const gaId = data.google_analytics_id;
        const a = document.createElement("script");
        a.async = true;
        a.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        a.setAttribute("data-injected", "head-scripts");
        document.head.appendChild(a);
        injected.push(a);
        const b = document.createElement("script");
        b.setAttribute("data-injected", "head-scripts");
        b.text = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${gaId}');`;
        document.head.appendChild(b);
        injected.push(b);
      }
      // Raw head HTML — parsed with DOMParser so embedded resource-loading tags
      // (e.g. <img onerror=...>) don't execute against the live document while we
      // inspect the markup. Only <script>/<link>/<meta>/<style> tags are forwarded
      // to <head>, and scripts are rebuilt into fresh <script> elements so they
      // execute in document context.
      if (data.head_scripts) {
        let parsed;
        try {
          parsed = new DOMParser().parseFromString(
            `<!doctype html><html><head>${data.head_scripts}</head><body></body></html>`,
            "text/html"
          );
        } catch (_e) {
          parsed = null;
        }
        const ALLOWED = new Set(["SCRIPT", "LINK", "META", "STYLE", "NOSCRIPT"]);
        const nodes = parsed ? Array.from(parsed.head.children) : [];
        nodes.forEach((node) => {
          if (!ALLOWED.has(node.tagName)) return;
          if (node.tagName === "SCRIPT") {
            // Rewrite to a real <script> element so it actually executes
            const s = document.createElement("script");
            for (const a of node.attributes) s.setAttribute(a.name, a.value);
            s.text = node.text || node.textContent || "";
            s.setAttribute("data-injected", "head-scripts");
            document.head.appendChild(s);
            injected.push(s);
          } else {
            const clone = node.cloneNode(true);
            clone.setAttribute("data-injected", "head-scripts");
            document.head.appendChild(clone);
            injected.push(clone);
          }
        });
      }
    }).catch(() => { /* settings optional */ });
    return () => {
      mounted = false;
      injected.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);
  return null;
}
